import { QuestionCategories } from "@sp-crm/core";
import { PrintableForm } from "components/printable-form/printable-form";
import { renderQuestionSection } from "components/printable-form/render-question-section";
import { AdvancedSearchEntityType } from "generated/graphql";
import { PlacementPage } from "components/feature";
import * as React from "react";
import { useBridgeQuestionsForCurrentRegion } from "store/selectors/bridge";
import { useTenantSettings } from "store/selectors/settings";
import { handleEvent } from "util/user-events";

export const IntakePrintable = () => {
    const {
        settings: { showClientFirst },
    } = useTenantSettings();
    const questions = useBridgeQuestionsForCurrentRegion(AdvancedSearchEntityType.Client);

    React.useEffect(() => {
        handleEvent("print-client-form");
    }, []);

    const clientSection = {
        title: "Client Information",
        fields: [
            { label: "Client Name", colSpan: 6 },
            { label: "Client Preferred Name", colSpan: 6 },
            { label: "Client Date of Birth", type: "date" as const, colSpan: 6 },
            { label: "Client Weight", colSpan: 3 },
            { label: "Client Height", colSpan: 3 },
            { label: "Client Cell Phone", colSpan: 3 },
            { label: "Client Other Phone", colSpan: 3 },
            { label: "Client Email", colSpan: 6 },
            {
                label: "Gender",
                colSpan: 6,
                type: "checkbox" as const,
                options: ["Female", "Male", "Other: ______________"],
            },
            {
                label: "Marital Status",
                colSpan: 6,
                type: "checkbox" as const,
                options: [
                    "Single",
                    "Married",
                    "Widow(er)",
                    "Domestic Partner",
                    "Non-married Couple",
                    "Divorced",
                    "Legally Separated",
                ],
            },
        ],
    };

    const currentLocationSection = {
        title: "Current Location",
        fields: [
            { label: "Current Location Address", colSpan: 12 },
            { label: "Current Location City", colSpan: 6 },
            { label: "Current Location State", colSpan: 3 },
            { label: "Current Location Zip", colSpan: 3 },
            { label: "Current Location Notes", colSpan: 12 },
        ],
    };

    const idealLocationSection = {
        title: "Ideal Location",
        fields: [
            { label: "Use Best Contact Address", colSpan: 12, type: "boolean" as const },
            { label: "Ideal Location Address", colSpan: 12 },
            { label: "Ideal Location City", colSpan: 6 },
            { label: "Ideal Location State", colSpan: 3 },
            { label: "Ideal Location Zip", colSpan: 3 },
            { label: "Ideal Location Notes", colSpan: 12 },
        ],
    };

    const bestContactSection = {
        title: "Best Contact Person",
        fields: [
            { label: "Best Contact Name", colSpan: 6 },
            { label: "Best Contact Preferred Name", colSpan: 6 },
            { label: "Best Contact Relationship", colSpan: 6 },
            { label: "Best Contact Cell Phone", colSpan: 3 },
            { label: "Best Contact Other Phone", colSpan: 3 },
            { label: "Best Contact Email", colSpan: 12 },
            { label: "Best Contact Address", colSpan: 12 },
            { label: "Best Contact City", colSpan: 6 },
            { label: "Best Contact State", colSpan: 3 },
            { label: "Best Contact Zip", colSpan: 3 },
            {
                label: "Power of Attorney",
                colSpan: 6,
                type: "checkbox" as const,
                options: [
                    "None",
                    "Full",
                    "Fiduciary",
                    "Medical",
                    "Financial",
                    "Guardian",
                ],
            },
        ],
    };

    const communityAttributesSection = renderQuestionSection(
        "Community Attributes",
        QuestionCategories.type,
        questions,
    );

    const healthSection = renderQuestionSection(
        "Health",
        QuestionCategories.physician,
        questions,
    );

    const financesSection = renderQuestionSection(
        "Finances",
        QuestionCategories.finance,
        questions,
    );

    financesSection.fields = [
        {
            label: "Monthly Budget",
            colSpan: 6,
        },
        {
            label: "Total Finances",
            colSpan: 6,
        },
        {
            label: "Private Pay Time Period",
            colSpan: 6,
            type: "checkbox" as const,
            options: ["None", "2+ years", "1+ year", "6+ months", "Direct Medicaid"],
        },
        ...financesSection.fields,
    ];

    const clientSummary = renderQuestionSection(
        "Client Summary",
        QuestionCategories.summary,
        questions,
    );

    const activitySection = renderQuestionSection(
        "Activities of Daily Living",
        QuestionCategories.activity,
        questions,
    );

    const servicesSection = renderQuestionSection(
        "Health Needs",
        QuestionCategories.services,
        questions,
    );

    const socialInterestsSection = renderQuestionSection(
        "Social / Personality",
        QuestionCategories.socialInterests,
        questions,
    );

    const insuranceSection = renderQuestionSection(
        "Insurance",
        QuestionCategories.insurance,
        questions,
    );

    const physicianSection = renderQuestionSection(
        "Physician",
        QuestionCategories.physician,
        questions,
    );

    const contractSection = renderQuestionSection(
        "Contract Details",
        QuestionCategories.contract,
        questions,
    );

    const staffSection = renderQuestionSection(
        "Desired Staffing",
        QuestionCategories.staff,
        questions,
    );

    const flexibleSection = renderQuestionSection(
        "Flexible",
        QuestionCategories.flexible,
        questions,
    );

    const communityRecordsSection = renderQuestionSection(
        "Community Records",
        QuestionCategories.communityRecords,
        questions,
    );

    const amenitySection = renderQuestionSection(
        "Amenities",
        QuestionCategories.amenity,
        questions,
    );

    const otherDetails = renderQuestionSection(
        "Other Details",
        QuestionCategories.otherDetails,
        questions,
    );

    const sections = [
        currentLocationSection,
        idealLocationSection,
        clientSummary,
        communityAttributesSection,
        healthSection,
        financesSection,
        otherDetails,
        activitySection,
        servicesSection,
        socialInterestsSection,
        insuranceSection,
        physicianSection,
        contractSection,
        staffSection,
        flexibleSection,
        communityRecordsSection,
        amenitySection,
    ].filter(section => section.fields.length > 0);

    if (showClientFirst) {
        sections.unshift(bestContactSection);
        sections.unshift(clientSection);
    } else {
        sections.unshift(clientSection);
        sections.unshift(bestContactSection);
    }

    return (
        <PlacementPage>
            <PrintableForm sections={sections} />
        </PlacementPage>
    );
};
